import { useState, useEffect } from 'react';
import { getSettings, getDismissedItems } from '../../utils/storage';
import { getRemainingCredits } from '../../utils/credits';
import { isNativeApp } from '../../utils/platform';
import { supabase } from '../../config/supabase';
import PricingModal from '../PricingModal';
import DashboardGhostRallyBanner from './DashboardGhostRallyBanner';
import { isNorthAmericaLocale } from '../../utils/regionDefaults';
import { isIOS } from '../../utils/platform';

const BANNER_DISMISSED_KEY = 'pm_desktop_banner_dismissed';
const UPGRADE_BANNER_DISMISSED_KEY = 'pm_upgrade_banner_dismissed';

const BADGE_CONFIG = {
  full_access: { label: 'Full Access', bg: 'rgba(211,71,74,0.15)', color: '#D3474A' },
  service_access: { label: 'Service Access', bg: 'rgba(59,130,246,0.15)', color: '#3B82F6' },
  log_access: { label: 'Log Access', bg: 'rgba(234,179,8,0.15)', color: '#EAB308' },
  view_only: { label: 'View Only', bg: 'rgba(156,163,175,0.15)', color: '#9CA3AF' },
};

export default function Dashboard({
  garages,
  vehicles,
  analyses,
  serviceHistory,
  onGoToGarage,
  onGoToVehicle,
  onOpenAutoAdvisor,
  onAddVehicle,
  pendingInvitations = [],
  pendingInvitationCount = 0,
  onAcceptInvitation,
  onDeclineInvitation,
  onUpgrade,
  reminders = [],
  onGoToEvents,
  todayCheckinEvents = [],
  onGoToEventDetail,
  onViewRoute,
  onGoToRoutes,
  onResumeNavigation,
}) {
  const [bannerDismissed, setBannerDismissed] = useState(
    () => localStorage.getItem(BANNER_DISMISSED_KEY) === 'true'
  );
  const [upgradeBannerDismissed, setUpgradeBannerDismissed] = useState(
    () => localStorage.getItem(UPGRADE_BANNER_DISMISSED_KEY) === 'true'
  );
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [invitationLoading, setInvitationLoading] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [activeNav, setActiveNav] = useState(() => {
    try {
      const raw = localStorage.getItem('pm_active_navigation');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  // Check Supabase for active route if localStorage doesn't have one (cross-session resume)
  useEffect(() => {
    if (activeNav) return; // already have one from localStorage
    supabase.auth.getUser().then(({ data }) => {
      if (!data?.user?.id) return;
      supabase.from('users').select('active_route_slug, active_route_title').eq('id', data.user.id).single()
        .then(({ data: profile }) => {
          if (profile?.active_route_slug) {
            const nav = { slug: profile.active_route_slug, title: profile.active_route_title || 'Route' };
            setActiveNav(nav);
            // Sync to localStorage so it works on next load too
            try { localStorage.setItem('pm_active_navigation', JSON.stringify(nav)); } catch {}
          }
        });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if user is on free plan
  const settings = getSettings();
  const isFreePlan = settings.subscription?.plan === 'free';
  const creditsRemaining = getRemainingCredits();
  const creditsTotal = settings.subscription?.creditsTotal || 15;

  function formatTime(timeStr) {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${m} ${ampm}`;
  }

  // Aggregate maintenance from ALL vehicles, filtering out dismissed/completed items
  const urgencyOrder = { high: 0, medium: 1, low: 2 };
  const allMaintenance = [];
  for (const v of vehicles) {
    const a = analyses[v.id];
    if (!a) continue;
    const label = `${v.year} ${v.make} ${v.model}`;
    const dismissedServices = getDismissedItems(v.id).map((d) => d.service);
    const completedServices = serviceHistory
      .filter((e) => e.vehicleId === v.id)
      .map((e) => e.service);
    for (const item of a.maintenanceItems) {
      if (dismissedServices.includes(item.service)) continue;
      if (completedServices.includes(item.service)) continue;
      allMaintenance.push({ ...item, vehicleId: v.id, vehicleLabel: label });
    }
  }
  allMaintenance.sort((a, b) => (urgencyOrder[a.urgency] ?? 2) - (urgencyOrder[b.urgency] ?? 2));

  // Urgent count across all vehicles
  const urgentCount = allMaintenance.filter((m) => m.urgency === 'high').length;

  // Recent log entries (last 5, sorted by date descending)
  const recentEntries = [...serviceHistory]
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
    .slice(0, 5)
    .map((entry) => {
      const v = vehicles.find((veh) => veh.id === entry.vehicleId);
      return {
        ...entry,
        vehicleLabel: v ? `${v.year} ${v.make} ${v.model}` : 'Unknown',
      };
    });

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  function formatCost(cost) {
    if (cost == null) return '';
    return `$${Number(cost).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }

  return (
    <div className="screen" style={{ paddingBottom: 100 }}>
      {/* Ghost Rally shortcut — visible when the user is in an in-progress rally */}
      <DashboardGhostRallyBanner />

      {/* Resume Navigation banner */}
      {activeNav && onResumeNavigation && (
        <div
          onClick={() => onResumeNavigation(activeNav.slug)}
          style={{
            background: 'rgba(211,71,74,0.12)',
            border: '1px solid rgba(211,71,74,0.3)',
            borderRadius: 'var(--radius-md, 8px)',
            padding: '14px 16px',
            marginBottom: 16,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <div style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: 'rgba(211,71,74,0.2)',
            border: '2px solid #D3474A',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D3474A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: '#D3474A',
              margin: 0,
            }}>
              Resume Navigation
            </p>
            <p style={{
              fontSize: '0.875rem',
              color: 'var(--text-primary)',
              margin: '2px 0 0',
              fontWeight: 600,
            }}>
              {activeNav.title}
            </p>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          {/* Mobile: logo image (no sidebar branding on mobile) */}
          <img
            className="mobile-only"
            src="/logo-wide.png"
            alt="Proper Miles"
            style={{ height: 28, objectFit: 'contain', display: 'block' }}
          />
          {/* Desktop: text title (logo is already in the sidebar) */}
          <h1
            className="screen-title desktop-only"
            style={{ fontSize: '1.25rem' }}
          >
            Proper Miles Mobile Garage
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginTop: 4 }}>
            Garages: {garages.length}&nbsp;&nbsp;&nbsp;&nbsp;Vehicles: {vehicles.length}
          </p>
        </div>

        {/* Plan badge */}
        <button
          onClick={() => setShowPricing(true)}
          style={{
            padding: '5px 12px',
            borderRadius: 100,
            border: 'none',
            background: isFreePlan
              ? 'rgba(156,163,175,0.12)'
              : 'rgba(211,71,74,0.12)',
            color: isFreePlan ? 'var(--text-muted)' : 'var(--accent)',
            fontSize: '0.75rem',
            fontWeight: 600,
            cursor: 'pointer',
            textTransform: 'capitalize',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            marginTop: 4,
          }}
        >
          {isFreePlan
            ? `Free \u00B7 ${creditsRemaining} left`
            : settings.subscription.plan}
        </button>
      </div>

      {/* Free plan upgrade banner — hidden in native app (Apple compliance) */}
      {/* Region gate: hide upgrade banner outside NA on web/Android (no Stripe
          B2C SaaS in those regions yet). iOS keeps it via Apple IAP. */}
      {isFreePlan && !upgradeBannerDismissed && !isNativeApp && (isNorthAmericaLocale() || isIOS) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '14px 18px',
            marginBottom: 16,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid rgba(211, 71, 74, 0.25)',
            background: 'linear-gradient(135deg, rgba(211, 71, 74, 0.06) 0%, rgba(211, 71, 74, 0.02) 100%)',
            position: 'relative',
          }}
        >
          {/* Dismiss button */}
          <button
            onClick={() => {
              setUpgradeBannerDismissed(true);
              localStorage.setItem(UPGRADE_BANNER_DISMISSED_KEY, 'true');
            }}
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
              fontSize: '0.875rem',
            }}
          >
            ×
          </button>

          <div style={{ flex: 1, paddingRight: 10 }}>
            <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 2 }}>
              Free Plan — {creditsRemaining}/{creditsTotal} credits remaining
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
              Upgrade for more vehicles, garages, and AI-powered credits.
            </div>
            <button
              onClick={onUpgrade}
              style={{
                marginTop: 8,
                padding: '6px 16px',
                borderRadius: 100,
                border: 'none',
                background: 'var(--accent)',
                color: '#fff',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer',
                letterSpacing: '0.02em',
              }}
            >
              View Plans
            </button>
          </div>
        </div>
      )}

      {/* Invitation banner — shown when pending invitations exist */}
      {pendingInvitationCount > 0 && (
        <button
          onClick={() => setShowInvitationModal(true)}
          className="card"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '14px 18px',
            marginBottom: 16,
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            border: '1px solid rgba(211, 71, 74, 0.3)',
            background: 'rgba(211, 71, 74, 0.06)',
          }}
        >
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(211, 71, 74, 0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {pendingInvitationCount} Garage Invitation{pendingInvitationCount !== 1 ? 's' : ''}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Tap to view and respond
            </div>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      )}

      {/* Event day banners — shown on event day for registered users */}
      {todayCheckinEvents.map((evt) => (
        <button
          key={evt.event_id}
          onClick={() => evt.route_slug ? onViewRoute?.(evt.route_slug) : onGoToEventDetail?.(evt.event_id)}
          className="card"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '14px 18px',
            marginBottom: 16,
            cursor: 'pointer',
            textAlign: 'left',
            width: '100%',
            border: '1px solid rgba(211, 71, 74, 0.3)',
            background: 'rgba(211, 71, 74, 0.06)',
          }}
        >
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(211, 71, 74, 0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D3474A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="3 11 22 2 13 21 11 13 3 11" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {evt.route_slug ? `Start Drive — ${evt.title}` : evt.title}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {evt.club_name}
              {evt.event_time ? ` · ${formatTime(evt.event_time)}` : ''}
              {evt.location_text ? ` · ${evt.location_text}` : ''}
            </div>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      ))}

      {/* Desktop banner — dismissible, only shows on wider screens */}
      {!bannerDismissed && (
        <div
          className="desktop-banner"
          style={{
            display: 'none',
            marginBottom: 20,
            padding: '14px 18px',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            position: 'relative',
          }}
        >
          <style>{`.desktop-banner { display: none !important; } @media (min-width: 768px) { .desktop-banner { display: flex !important; } }`}</style>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 4 }}>
              Welcome to Proper Miles Mobile Garage
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              Manage your vehicles, track maintenance, and get expert insights — all in one place.
              Visit <a href="https://propermiles.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }}>propermiles.com</a> for more.
            </div>
          </div>
          <button
            onClick={() => {
              setBannerDismissed(true);
              localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
            }}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
              fontSize: '1.125rem',
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Quick Actions — 5 buttons */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
          Quick Actions
        </h2>
        <div className="desktop-grid-2" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Log Mileage / Service — go directly to vehicle detail if only 1 vehicle */}
          <QuickActionButton
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/></svg>}
            iconBg="rgba(34, 197, 94, 0.12)"
            title="Log Mileage / Service"
            subtitle={vehicles.length === 1 ? vehicles[0].year + ' ' + vehicles[0].make + ' ' + vehicles[0].model : 'Select a vehicle to log'}
            onClick={() => {
              if (vehicles.length === 1) {
                onGoToVehicle(vehicles[0].id);
              } else if (garages.length > 0) {
                onGoToGarage(garages[0].id);
              }
            }}
          />
          {/* Pre-Purchase Research (formerly Research Vehicle) */}
          <QuickActionButton
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>}
            iconBg="var(--accent-subtle)"
            title="Pre-Purchase Research"
            subtitle="Pre-purchase analysis"
            onClick={() => onOpenAutoAdvisor('purchase')}
          />
          {/* Check Repair Quote */}
          <QuickActionButton
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>}
            iconBg="rgba(59, 130, 246, 0.12)"
            title="Check Repair Quote"
            subtitle="Verify quote fairness"
            onClick={() => onOpenAutoAdvisor('repair')}
          />
          {/* Create Route */}
          {onGoToRoutes && (
            <QuickActionButton
              icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>}
              iconBg="rgba(245, 158, 11, 0.12)"
              title="Create Route"
              subtitle="Build a driving route"
              onClick={onGoToRoutes}
            />
          )}
          {/* Drivers Club Events */}
          {onGoToEvents && (
            <QuickActionButton
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              }
              iconBg="rgba(139, 92, 246, 0.12)"
              title="See Upcoming Events"
              subtitle="Drivers Club events calendar"
              onClick={onGoToEvents}
            />
          )}
        </div>
      </div>

      {/* Stale Analysis — vehicles that need a refresh */}
      {(() => {
        const staleVehicles = vehicles.filter((v) => {
          const a = analyses[v.id];
          if (!a || !a.analyzedAt) return false;
          const daysSince = (Date.now() - new Date(a.analyzedAt).getTime()) / (1000 * 60 * 60 * 24);
          return daysSince > 30;
        });
        if (staleVehicles.length === 0) return null;
        return (
          <div style={{ marginBottom: 20 }}>
            {staleVehicles.map((v) => {
              const a = analyses[v.id];
              const daysSince = Math.floor((Date.now() - new Date(a.analyzedAt).getTime()) / (1000 * 60 * 60 * 24));
              const vLabel = `${v.year} ${v.make} ${v.model}`;
              return (
                <button
                  key={v.id}
                  onClick={() => onGoToVehicle(v.id)}
                  className="card"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: '14px 18px',
                    marginBottom: 8,
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    background: 'rgba(245, 158, 11, 0.04)',
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'rgba(245, 158, 11, 0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10" />
                      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                    </svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      Update Available — {vLabel}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      Last analyzed {daysSince} days ago
                    </div>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* Your Reminders — next 3 upcoming across all vehicles */}
      {(() => {
        const upcomingReminders = reminders
          .filter((r) => r.status === 'pending')
          .sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''))
          .slice(0, 3);

        if (upcomingReminders.length === 0) return null;

        function formatReminderDate(dateStr) {
          if (!dateStr) return '';
          const today = new Date().toISOString().split('T')[0];
          const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
          if (dateStr === today) return 'Today';
          if (dateStr === tomorrow) return 'Tomorrow';
          if (dateStr < today) {
            const days = Math.ceil((new Date(today) - new Date(dateStr)) / 86400000);
            return `${days}d overdue`;
          }
          return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        }

        return (
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
              Your Reminders
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {upcomingReminders.map((reminder) => {
                const v = vehicles.find((veh) => veh.id === reminder.vehicleId);
                const vehicleLabel = v ? `${v.year} ${v.make} ${v.model}` : 'Vehicle';
                const isOverdue = reminder.dueDate && reminder.dueDate < new Date().toISOString().split('T')[0];

                return (
                  <button
                    key={reminder.id}
                    onClick={() => onGoToVehicle(reminder.vehicleId)}
                    className="card"
                    style={{ padding: '14px 16px', textAlign: 'left', cursor: 'pointer', width: '100%' }}
                  >
                    <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>
                      {vehicleLabel}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{reminder.title}</span>
                          {isOverdue && (
                            <span style={{
                              fontSize: '0.625rem', fontWeight: 700, padding: '2px 7px', borderRadius: 100,
                              background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', textTransform: 'uppercase',
                            }}>
                              overdue
                            </span>
                          )}
                        </div>
                      </div>
                      <span style={{
                        fontSize: '0.8125rem', fontWeight: 600, flexShrink: 0,
                        color: isOverdue ? '#ef4444' : 'var(--text-secondary)',
                      }}>
                        {formatReminderDate(reminder.dueDate)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Attention Needed — urgent alert + top items */}
      {allMaintenance.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            Attention Needed
          </h2>

          {/* Urgent alert card */}
          {urgentCount > 0 && (
            <div
              className="card"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: '50%',
                  background: 'rgba(239, 68, 68, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                  {urgentCount} Urgent {urgentCount === 1 ? 'Item' : 'Items'}
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                  {allMaintenance.length} maintenance items across {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          )}

          {/* Top 3 maintenance items — clickable to vehicle detail */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {allMaintenance.slice(0, 3).map((item, i) => (
              <button
                key={i}
                onClick={() => onGoToVehicle(item.vehicleId)}
                className="card"
                style={{ padding: '14px 16px', textAlign: 'left', cursor: 'pointer', width: '100%' }}
              >
                <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>
                  {item.vehicleLabel}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{item.service}</span>
                      <span className={`badge badge-${item.urgency}`} style={{ fontSize: '0.6875rem', padding: '2px 7px' }}>
                        {item.urgency}
                      </span>
                    </div>
                  </div>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', flexShrink: 0 }}>
                    {item.costDIY || item.costShop || ''}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Recent Log Entries */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
          Recent Log Entries
        </h2>
        <div className="card" style={{ padding: '4px 16px' }}>
          {recentEntries.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem', padding: '16px 0' }}>
              <p>No services logged yet.</p>
            </div>
          ) : (
            recentEntries.map((entry) => (
              <button
                key={entry.id}
                onClick={() => onGoToVehicle(entry.vehicleId)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  padding: '10px 0',
                  background: 'none',
                  border: 'none',
                  borderBottom: '1px solid var(--border)',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: 'inherit',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginBottom: 2 }}>
                    {entry.vehicleLabel}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: '0.8125rem' }}>{entry.service}</span>
                    {entry.cost != null && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--accent)' }}>
                        {formatCost(entry.cost)}
                      </span>
                    )}
                  </div>
                </div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', flexShrink: 0 }}>
                  {formatDate(entry.date)}
                </span>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Invitation Modal */}
      {showInvitationModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 200,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowInvitationModal(false); }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 480,
              maxHeight: '80vh',
              background: 'var(--bg-card)',
              borderRadius: '16px 16px 0 0',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Modal header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px',
              borderBottom: '1px solid var(--border)',
            }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Garage Invitations</h2>
              <button
                onClick={() => setShowInvitationModal(false)}
                style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'var(--bg-elevated)', border: 'none',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text-secondary)', fontSize: '1.125rem',
                }}
              >×</button>
            </div>

            {/* Invitation list */}
            <div style={{ overflowY: 'auto', padding: '16px 20px', flex: 1 }}>
              {pendingInvitations.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px 0', fontSize: '0.875rem' }}>
                  No pending invitations
                </div>
              ) : (
                pendingInvitations.map((inv) => {
                  const badge = BADGE_CONFIG[inv.permission_level] || BADGE_CONFIG.view_only;
                  const isLoading = invitationLoading === inv.id;
                  return (
                    <div
                      key={inv.id}
                      className="card"
                      style={{ padding: '16px', marginBottom: 12 }}
                    >
                      {/* Garage name + permission badge */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.9375rem', flex: 1, minWidth: 0 }}>
                          {inv.garages?.name || 'Unknown Garage'}
                        </div>
                        <span style={{
                          fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '0.04em', padding: '3px 8px', borderRadius: 6,
                          background: badge.bg, color: badge.color, flexShrink: 0,
                        }}>
                          {badge.label}
                        </span>
                      </div>

                      {/* Inviter */}
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: inv.message ? 6 : 12 }}>
                        Invited by {inv.inviterName || 'Unknown'}
                      </div>

                      {/* Optional message */}
                      {inv.message && (
                        <div style={{
                          fontSize: '0.8125rem', color: 'var(--text-secondary)',
                          fontStyle: 'italic', marginBottom: 12,
                          padding: '8px 12px', borderRadius: 8,
                          background: 'var(--bg-elevated)',
                        }}>
                          "{inv.message}"
                        </div>
                      )}

                      {/* Accept / Decline buttons */}
                      <div style={{ display: 'flex', gap: 10 }}>
                        <button
                          onClick={async () => {
                            setInvitationLoading(inv.id);
                            await onAcceptInvitation(inv.id);
                            setInvitationLoading(null);
                            // Auto-close if no more invitations
                            if (pendingInvitations.length <= 1) setShowInvitationModal(false);
                          }}
                          disabled={isLoading}
                          style={{
                            flex: 1, padding: '10px 0', borderRadius: 8,
                            background: 'var(--accent)', color: '#fff',
                            fontWeight: 600, fontSize: '0.8125rem',
                            border: 'none', cursor: isLoading ? 'not-allowed' : 'pointer',
                            opacity: isLoading ? 0.6 : 1,
                          }}
                        >
                          {isLoading ? 'Accepting…' : 'Accept'}
                        </button>
                        <button
                          onClick={async () => {
                            setInvitationLoading(inv.id);
                            await onDeclineInvitation(inv.id);
                            setInvitationLoading(null);
                            if (pendingInvitations.length <= 1) setShowInvitationModal(false);
                          }}
                          disabled={isLoading}
                          style={{
                            flex: 1, padding: '10px 0', borderRadius: 8,
                            background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
                            fontWeight: 600, fontSize: '0.8125rem',
                            border: '1px solid var(--border)', cursor: isLoading ? 'not-allowed' : 'pointer',
                            opacity: isLoading ? 0.6 : 1,
                          }}
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Plan change modal */}
      {showPricing && (
        <PricingModal
          onClose={() => setShowPricing(false)}
          onToast={() => {}}
        />
      )}
    </div>
  );
}

/* ─── Quick Action Button ─── */

function QuickActionButton({ icon, iconBg, title, subtitle, onClick, href }) {
  const content = (
    <>
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 'var(--radius-sm)',
          background: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{title}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{subtitle}</div>
      </div>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </>
  );

  const sharedStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 18px',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    textDecoration: 'none',
    color: 'inherit',
  };

  // When href is provided, render a real <a> so the native WebView can intercept the tap
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="card" style={sharedStyle}>
        {content}
      </a>
    );
  }

  return (
    <button onClick={onClick} className="card" style={sharedStyle}>
      {content}
    </button>
  );
}
